/**
 * Get Badge variant for a given workspace role. Maps to the semantic
 * variants defined in `theme/components.ts`.
 */
export function getRoleBadgeVariant(role: string): 'brand' | 'warning' | 'info' | 'neutral' {
  if (!role) return 'neutral';
  switch (role.toLowerCase()) {
    case 'owner':
      return 'brand';
    case 'admin':
      return 'warning';
    case 'member':
      return 'info';
    // GUEST is a first-class Prisma role, not an unknown one. It reached
    // 'neutral' via `default:` before, which was right by coincidence.
    case 'viewer':
    case 'guest':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/**
 * Human label for a workspace role.
 *
 * The Badge theme used to `text-transform: uppercase` everything, so call
 * sites could render the raw Prisma enum (`OWNER`) and get a badge that
 * looked deliberate. Badges are sentence case since the 2026-09 lozenge
 * pass, which means the enum now has to be turned into a label somewhere —
 * and it belongs HERE, next to the variant it is always shown with, not
 * spelled out again at each surface.
 */
export function getRoleLabel(role: string): string {
  // 'Unknown', NOT 'Member'. This is a permissions surface: naming a concrete
  // role for a value we do not have asserts an access level the person may not
  // hold. It also has to agree with its sibling — `getRoleBadgeVariant('')`
  // returns 'neutral', the VIEWER/GUEST styling, so 'Member' here would paint a
  // badge reading "Member" in the colour reserved for the least-privileged
  // roles, while a real MEMBER renders 'info'. Same word, two colours, one of
  // them invented. Pinned alongside the variant table so the pair cannot drift.
  if (!role) return 'Unknown';
  const lower = role.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Check if user can edit workspace settings (admin/owner only)
 */
export function canEditWorkspace(workspace: any): boolean {
  const role = workspace?.userRole?.toLowerCase?.();
  return role === 'admin' || role === 'owner';
}

/**
 * Check if role change is safe (prevents leaving workspace without admin/owner)
 */
export function canChangeRole(
  workspaceId: string,
  userId: string,
  newRole: string,
  workspaceMembers: Record<string, any[]>,
): boolean {
  const members = workspaceMembers[workspaceId] || [];
  const currentMember = members.find((m) => m.id === userId);

  if (!currentMember) return false;

  const currentRole = currentMember.role?.toLowerCase?.();
  const targetRole = newRole?.toLowerCase?.();

  // If not downgrading from admin/owner, allow the change
  if (!currentRole || !['owner', 'admin'].includes(currentRole)) return true;
  if (!targetRole || ['owner', 'admin'].includes(targetRole)) return true;

  // Count current admins/owners (excluding the user being changed)
  const adminCount = members.filter(
    (m) => m.id !== userId && m.role && ['owner', 'admin'].includes(m.role.toLowerCase()),
  ).length;

  // Must have at least one other admin/owner to downgrade this user
  return adminCount > 0;
}

/**
 * Get disabled roles for a user (to prevent unsafe changes)
 */
export function getDisabledRoles(
  workspaceId: string,
  userId: string,
  workspaceMembers: Record<string, any[]>,
): string[] {
  const members = workspaceMembers[workspaceId] || [];
  const currentMember = members.find((m) => m.id === userId);

  if (!currentMember) return [];

  const currentRole = currentMember.role?.toLowerCase?.();

  // If not admin/owner, no restrictions
  if (!currentRole || !['owner', 'admin'].includes(currentRole)) return [];

  // Count other admins/owners
  const otherAdminCount = members.filter(
    (m) => m.id !== userId && m.role && ['owner', 'admin'].includes(m.role.toLowerCase()),
  ).length;

  // If this is the last admin/owner, disable downgrade options
  if (otherAdminCount === 0) {
    return ['MEMBER', 'VIEWER'];
  }

  return [];
}
